import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { Shield, ArrowRight, CreditCard, FileX, AlertTriangle, Scale, PhoneOff, FileText, Phone } from 'lucide-react';

const services = [
  {
    icon: CreditCard,
    title: 'IRS Payment Plans',
    description: 'Installment agreements that let you pay your tax debt over time. We negotiate terms that work with your financial situation.',
  },
  {
    icon: Scale,
    title: 'Penalty Relief',
    description: 'First-time abatement, reasonable cause, and other strategies to reduce or eliminate penalties and interest.',
  },
  {
    icon: FileX,
    title: 'Unfiled Returns',
    description: 'Preparation and filing of past-due tax returns. We identify the best approach to bring you into compliance.',
  },
  {
    icon: FileText,
    title: 'Notice Response',
    description: 'Professional response to IRS notices, including CP2000, CP14, CP501, and collection notices.',
  },
  {
    icon: PhoneOff,
    title: 'Collections Help',
    description: 'Assistance with wage garnishments, bank levies, and other collection actions. We work to protect your assets.',
  },
  {
    icon: Shield,
    title: 'Resolution Guidance',
    description: 'Full-service guidance through the resolution process, from initial review to final resolution with the IRS.',
  },
];

export default function TaxResolution() {
  useSEO({
    title: 'Tax Resolution Services',
    description: 'Full-service tax resolution: IRS payment plans, penalty relief, unfiled returns, notice response, and collections help.',
  });

  return (
    <div data-testid="tax-resolution-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              Tax Resolution Services
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              Full-Service Help When the Problem Requires It
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              If your matter needs additional work beyond a diagnostic review, we provide comprehensive tax resolution services to help you resolve your tax issues with the IRS.
            </p>
            <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="resolution-hero-cta"
                className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
              >
                Book a Tax Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              What We Offer
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Comprehensive tax resolution for individuals and businesses
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {services.map((service, i) => (
              <AnimatedSection key={i} delay={i * 0.07}>
                <div className="bg-white p-8 h-full">
                  <div className="w-11 h-11 rounded-md bg-brand-navy/5 flex items-center justify-center mb-5">
                    <service.icon className="w-5 h-5 text-brand-navy" />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-brand-navy mb-3">{service.title}</h3>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">{service.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              How We Work
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
              Resolution starts with understanding
            </h2>
            <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-8">
              We don't push one-size-fits-all solutions. Every resolution case begins with a thorough review of your specific situation. Most clients come to us after completing a Tax Diagnostic Review, which gives us the information we need to recommend the right path forward.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4 p-5 bg-white border border-slate-200 rounded-lg">
                <span className="font-heading font-bold text-lg text-brand-green">1</span>
                <div>
                  <h4 className="font-heading font-semibold text-sm text-brand-navy mb-1">Diagnostic Review</h4>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222]">We review your documents, transcripts, and tax history to understand the full picture.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-white border border-slate-200 rounded-lg">
                <span className="font-heading font-bold text-lg text-brand-green">2</span>
                <div>
                  <h4 className="font-heading font-semibold text-sm text-brand-navy mb-1">Resolution Plan</h4>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222]">We present your options with realistic expectations, timelines, and costs.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-white border border-slate-200 rounded-lg">
                <span className="font-heading font-bold text-lg text-brand-green">3</span>
                <div>
                  <h4 className="font-heading font-semibold text-sm text-brand-navy mb-1">Execution</h4>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222]">We handle the IRS on your behalf — filings, negotiations, and follow-up until the matter is resolved.</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Not sure where to start?
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Begin with a question or a diagnostic review. You don't have to commit to full resolution until you're ready.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/ask">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-6 text-base rounded-md">
                  Ask a Question First
                </Button>
              </Link>
              <Link to="/services/diagnostic">
                <Button className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md">
                  Start a Tax Diagnostic
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
